import { useState, useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Download, FileText, AlertCircle, Loader2 } from 'lucide-react'
import { usePreviewFile, useServeFile } from '../hooks'

interface FilePreviewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    assessmentId: string
    questionId: string
    filename: string
}

export function FilePreviewDialog({
    open,
    onOpenChange,
    assessmentId,
    questionId,
    filename,
}: FilePreviewDialogProps) {
    const [blobUrl, setBlobUrl] = useState<string | null>(null)
    const [fileType, setFileType] = useState<string | null>(null)
    
    const previewMutation = usePreviewFile()
    const downloadMutation = useServeFile()

    useEffect(() => {
        if (open && assessmentId && questionId) {
            handleLoadPreview()
        }
        return () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl)
                setBlobUrl(null)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, assessmentId, questionId])

    const handleLoadPreview = async () => {
        try {
            const res = await previewMutation.mutateAsync({ assessmentId, questionId })
            const blob = res.data
            const url = URL.createObjectURL(blob)
            setBlobUrl(url)
            setFileType(blob.type)
        } catch (err) {
            console.error('Failed to load preview:', err)
        }
    }

    const handleDownload = async () => {
        try {
            const response = await downloadMutation.mutateAsync({ assessmentId, questionId })
            const blob: Blob = response.data
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (err) {
            console.error('Download failed:', err)
        }
    }

    const renderPreview = () => {
        if (previewMutation.isPending) {
            return (
                <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <p className="text-sm font-medium">Loading preview...</p>
                </div>
            )
        }

        if (previewMutation.isError) {
            return (
                <div className="flex flex-col items-center justify-center h-64 gap-3 text-destructive p-6 text-center">
                    <AlertCircle className="h-10 w-10 opacity-50" />
                    <p className="font-medium text-balance leading-relaxed">
                        Could not load file preview. The file may have been moved or the server is unavailable.
                    </p>
                </div>
            )
        }

        if (!blobUrl) return null

        const ext = filename.split('.').pop()?.toLowerCase() || ''
        
        // Image Viewer
        if (fileType?.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
            return (
                <div className="flex items-center justify-center p-2 bg-muted/20 rounded-md overflow-hidden">
                    <img 
                        src={blobUrl} 
                        alt={filename} 
                        className="max-w-full max-h-[60vh] object-contain shadow-sm" 
                    />
                </div>
            )
        }

        // PDF Viewer
        if (fileType === 'application/pdf' || ext === 'pdf') {
            return (
                <div className="w-full h-[60vh] border rounded-md overflow-hidden bg-muted/10">
                    <iframe 
                        src={`${blobUrl}#toolbar=0&navpanes=0`} 
                        className="w-full h-full" 
                        title={filename}
                    />
                </div>
            )
        }

        // Video Viewer
        if (fileType?.startsWith('video/') || ['mp4', 'webm', 'ogg'].includes(ext)) {
            return (
                <div className="w-full aspect-video border rounded-md overflow-hidden bg-black flex items-center justify-center">
                    <video 
                        src={blobUrl} 
                        controls 
                        className="max-w-full max-h-full"
                    />
                </div>
            )
        }

        // Audio Viewer
        if (fileType?.startsWith('audio/') || ['mp3', 'wav', 'ogg'].includes(ext)) {
            return (
                <div className="w-full py-12 flex items-center justify-center bg-muted/20 rounded-md">
                    <audio 
                        src={blobUrl} 
                        controls 
                        className="w-4/5"
                    />
                </div>
            )
        }

        // Text Viewer (txt, json, csv, etc.)
        if (fileType?.startsWith('text/') || ['txt', 'json', 'csv', 'md', 'xml', 'sql'].includes(ext)) {
            return (
                <div className="w-full h-[60vh] overflow-auto p-4 bg-slate-950 font-mono text-xs text-slate-100 rounded-md whitespace-pre">
                    <FetchTextContent url={blobUrl} />
                </div>
            )
        }

        // Fallback for Office docs, Zips, etc.
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4 bg-muted/10 rounded-md border border-dashed p-6 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                    <FileText className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                    <p className="font-medium text-sm">No Preview Available</p>
                    <p className="text-xs text-muted-foreground text-balance">
                        This file type (.{ext}) cannot be previewed in the browser. 
                        Please download it to view the content.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleDownload} className="gap-2">
                    <Download className="h-3.5 w-3.5" />
                    Download File
                </Button>
            </div>
        )
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-6 gap-4">
                <DialogHeader className="flex flex-row items-center justify-between border-b pb-4 shrink-0">
                    <div className="space-y-0.5 pr-8 truncate">
                        <DialogTitle className="text-lg font-semibold truncate leading-tight">
                            {filename}
                        </DialogTitle>
                        <p className="text-[11px] text-muted-foreground font-mono opacity-70">
                            Assessment Evidence Preview
                        </p>
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-hidden min-h-0">
                    {renderPreview()}
                </div>

                <DialogFooter className="border-t pt-4 sm:justify-end gap-2 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="text-xs">
                        Close
                    </Button>
                    <Button 
                        size="sm" 
                        onClick={handleDownload} 
                        className="gap-2 text-xs"
                        disabled={downloadMutation.isPending}
                    >
                        {downloadMutation.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Download className="h-3.5 w-3.5" />
                        )}
                        Download
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

function FetchTextContent({ url }: { url: string }) {
    const [content, setContent] = useState<string>('Loading text content...')
    
    useEffect(() => {
        fetch(url)
            .then(res => res.text())
            .then(text => setContent(text))
            .catch(err => setContent(`Error loading text: ${err.message}`))
    }, [url])

    return <code>{content}</code>
}
